import { useEffect, useState } from 'react';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';

export default function Directions({ origin, tripStops, onRouteFound, onRouteClear }) {
  const map = useMap();
  const routesLibrary = useMapsLibrary('routes');
  const [directionsService, setDirectionsService] = useState();
  const [directionsRenderer, setDirectionsRenderer] = useState();

  // Initialize directions service and renderer
  useEffect(() => {
    if (!routesLibrary || !map) return;
    setDirectionsService(new routesLibrary.DirectionsService());
    setDirectionsRenderer(new routesLibrary.DirectionsRenderer({ 
      map,
      suppressMarkers: true, // We already have our custom markers
      polylineOptions: {
        strokeColor: '#3b82f6',
        strokeWeight: 5,
        strokeOpacity: 0.8
      }
    }));
  }, [routesLibrary, map]);

  // Use directions service
  useEffect(() => {
    if (!directionsService || !directionsRenderer) return;

    if (!origin || !tripStops || tripStops.length === 0) {
      directionsRenderer.setDirections({routes: []});
      return;
    }

    // The last stop is the destination
    const destination = { lat: parseFloat(tripStops[tripStops.length - 1].lat), lng: parseFloat(tripStops[tripStops.length - 1].lng) };
    
    // Any intermediate stops are waypoints
    const waypoints = tripStops.slice(0, -1).map(stop => ({
      location: { lat: parseFloat(stop.lat), lng: parseFloat(stop.lng) },
      stopover: true
    }));

    directionsService
      .route({
        origin: origin,
        destination: destination,
        waypoints: waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false,
        optimizeWaypoints: true // Optimize the order of waypoints for the shortest route
      })
      .then(response => {
        directionsRenderer.setDirections(response);
        if (response.routes && response.routes.length > 0) {
          const route = response.routes[0];
          
          // Sum up total distance and duration across all legs
          let totalDistanceMeters = 0;
          let totalDurationSeconds = 0;
          
          if (route.legs) {
            route.legs.forEach(leg => {
              totalDistanceMeters += leg.distance.value;
              totalDurationSeconds += leg.duration.value;
            });
          }
          
          // Format distance
          const formatDist = (val) => val >= 1000 ? `${(val / 1000).toFixed(1)} km` : `${val} m`;
          
          // Format duration
          const formatDur = (val) => {
            const h = Math.floor(val / 3600);
            const m = Math.floor((val % 3600) / 60);
            return h > 0 ? `${h} ชม. ${m} นาที` : `${m} นาที`;
          };

          const formattedLegs = route.legs ? route.legs.map((leg, index) => ({
            legIndex: index,
            distance: formatDist(leg.distance.value),
            duration: formatDur(leg.duration.value)
          })) : [];

          if (onRouteFound) {
            onRouteFound({
              distance: formatDist(totalDistanceMeters),
              duration: formatDur(totalDurationSeconds),
              legs: formattedLegs
            });
          }
        }
      })
      .catch(e => {
        console.error('Directions request failed:', e);
        alert(`ไม่สามารถคำนวณเส้นทางได้ Error: ${e.message || e.code || 'Unknown Error'}\n\nคำแนะนำ: กรุณาไปที่ Google Cloud Console แล้วเปิดการใช้งาน "Directions API"`);
        if (onRouteClear) onRouteClear();
      });

    // Cleanup when origin/destination change or unmount
    return () => {
      directionsRenderer.setDirections({routes: []});
    };
  }, [directionsService, directionsRenderer, origin, tripStops]);

  return null;
}
